import React, { useState, useEffect } from 'react';
import { MessageCircle, Phone, X } from 'lucide-react';
import { CONTACT } from '@/constants/config';

interface ZaloButtonProps {
  message?: string;
}

const ZaloButton: React.FC<ZaloButtonProps> = ({ 
  message = "Xin chào! Tôi quan tâm đến dịch vụ quản lý căn hộ cho thuê của Propico. Bạn có thể giúp tôi biết thêm thông tin về cách hoạt động cho bất động sản của tôi không?" 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  
  const handleZaloClick = () => {
    const encodedMessage = encodeURIComponent(message);
    const zaloUrl = `https://zalo.me/${CONTACT.ZALO}?text=${encodedMessage}`;
    window.open(zaloUrl, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = `tel:+${CONTACT.HOTLINE}`;
  };

  if (isMobile) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.1)] safe-area-bottom">
        <div className="grid grid-cols-2 gap-0">
          <button
            onClick={handleCallClick}
            className="flex items-center justify-center gap-2.5 py-3.5 text-foreground hover:bg-muted/50 active:bg-muted transition-colors border-r border-border"
            aria-label="Gọi hotline"
          >
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Phone size={18} className="text-primary" />
            </div>
            <div className="text-left">
              <p className="text-[11px] text-muted-foreground leading-tight">Hotline</p>
              <p className="text-sm font-semibold leading-tight">{CONTACT.HOTLINE_DISPLAY}</p>
            </div>
          </button>
          
          <button
            onClick={handleZaloClick}
            className="flex items-center justify-center gap-2.5 py-3.5 text-white bg-[#0068FF] hover:bg-[#0052CC] active:bg-[#004AB8] transition-colors"
            aria-label="Chat qua Zalo"
          >
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle size={18} />
            </div>
            <div className="text-left">
              <p className="text-[11px] text-white/70 leading-tight">Chat ngay</p>
              <p className="text-sm font-semibold leading-tight">Zalo</p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      <div className={`flex flex-col gap-2 transition-all duration-300 ${isExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <button
          onClick={handleCallClick}
          className="flex items-center gap-3 bg-card border border-border rounded-full pl-4 pr-5 py-2.5 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 group"
          aria-label="Gọi hotline"
        >
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Phone size={18} className="text-primary" />
          </div>
          <div className="text-left">
            <p className="text-xs text-muted-foreground">Gọi miễn phí</p>
            <p className="text-sm font-semibold text-foreground">{CONTACT.HOTLINE_DISPLAY}</p>
          </div>
        </button>

        <button
          onClick={handleZaloClick}
          className="flex items-center gap-3 bg-[#0068FF] text-white rounded-full pl-4 pr-5 py-2.5 shadow-lg hover:shadow-xl hover:bg-[#0052CC] transition-all duration-200 hover:scale-105"
          aria-label="Chat qua Zalo"
        >
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <MessageCircle size={18} />
          </div>
          <div className="text-left">
            <p className="text-xs text-white/70">Chat ngay</p>
            <p className="text-sm font-semibold">Zalo</p>
          </div>
        </button>
      </div>

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center ${
          isExpanded 
            ? 'bg-muted text-foreground hover:bg-muted/80 rotate-0' 
            : 'bg-[#0068FF] text-white hover:bg-[#0052CC] animate-bounce-gentle'
        }`}
        aria-label={isExpanded ? "Đóng" : "Liên hệ"}
      >
        {isExpanded ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {!isExpanded && (
        <div className="absolute bottom-0 right-0 w-14 h-14 rounded-full bg-[#0068FF]/30 animate-ping pointer-events-none" />
      )}
    </div>
  );
};

export default ZaloButton;
